import React, { useState } from "react";
import { Control, FieldErrors } from "react-hook-form";
import { DropdownOption, ensure, FormAutocomplete, ValidateRule } from "sprint-common-web";

import {
    SuggestContentListByTitleResponseAncillary,
    SuggestContentListByTitleResponseAncillaryVersion,
    SuggestContentListByTitleResponseItem,
    SuggestContentListByTitleResponseMovie,
    SuggestContentListByTitleResponseMovieVersion,
    SuggestContentListByTitleResponseTvEpisode,
    SuggestContentListByTitleResponseTvEpisodeVersion,
    SuggestContentListByTitleResponseTvSeason,
    SuggestContentListByTitleResponseTvSeries
} from "../../../common/api/catalogue-api/contents-api/SuggestContentListByTitleResponse";
import { useCatalogueContentSearchApi } from "../../../common/api/catalogue-api/contents-api/useCatalogueContentSearchApi";
import { ContentSource } from "../../types/ContentSource";
import { ContentSubtype } from "../../types/ContentSubtype";
import { handleOptionChange } from "../../utils/handleOptionChange";

export interface CrossTypeContentSearch {
    initOptions: DropdownOption[];
    onSelect(selectedOption: CrossTypeContentSearchItem): void;
    onClear(): void;
    required?: boolean;
    control: Control;
    errors: FieldErrors;
    name: string;
    testId: string;
    label: string;
    placeholder: string;
    disabled?: boolean;
    validate?: ValidateRule<string> | undefined;
}

export type CrossTypeContentSearchItem =
    | CrossTypeContentSearchMovieItem
    | CrossTypeContentSearchTvSeriesItem
    | CrossTypeContentSearchAncillaryItem;

export type CrossTypeContentSearchMovieItem = SuggestContentListByTitleResponseMovie;
export type CrossTypeContentSearchMovieItemVersion = SuggestContentListByTitleResponseMovieVersion;

export type CrossTypeContentSearchTvSeriesItem = SuggestContentListByTitleResponseTvSeries;
export type CrossTypeContentSearchTvSeriesItemSeason = SuggestContentListByTitleResponseTvSeason;
export type CrossTypeContentSearchTvSeriesItemEpisode = SuggestContentListByTitleResponseTvEpisode;
export type CrossTypeContentSearchTvSeriesItemEpisodeVersion = SuggestContentListByTitleResponseTvEpisodeVersion;

export type CrossTypeContentSearchAncillaryItem = SuggestContentListByTitleResponseAncillary;
export type CrossTypeContentSearchAncillaryVersion = SuggestContentListByTitleResponseAncillaryVersion;

const MIN_QUERY_LENGTH = 3;

export const CrossTypeContentSearch = (props: CrossTypeContentSearch): JSX.Element => {
    const { suggestContentListByTitle } = useCatalogueContentSearchApi();

    const [contentItems, setContentItems] = useState<CrossTypeContentSearchItem[]>([]);

    const handleTitleCustomSearch = async (query: string): Promise<DropdownOption[]> => {
        if (query.length < MIN_QUERY_LENGTH) return [];

        const response = await suggestContentListByTitle(query);

        setContentItems(response.items);

        return response.items.map((item) => mapToDropdownOption(item));
    };

    const handleSelect = (contentId: string) => {
        const selection = contentItems.find((c) => c.id === contentId);

        ensure(selection, `Content with ID ${contentId} missing among available items`);

        props.onSelect(selection);
    };

    return (
        <FormAutocomplete
            testId={props.testId}
            label={props.label}
            name={props.name}
            minSearchLength={MIN_QUERY_LENGTH}
            disabled={props.disabled}
            initOptions={props.initOptions}
            updateInitialOptionsUponTheirChange
            rules={{
                required: props.required ? "Please select content title" : false,
                validate: props.validate ?? undefined
            }}
            placeholder={props.placeholder}
            control={props.control}
            errors={props.errors}
            onCustomSearch={handleTitleCustomSearch}
            onChange={handleOptionChange(handleSelect, props.onClear)}
        />
    );
};

const mapToDropdownOption = (content: SuggestContentListByTitleResponseItem): DropdownOption => {
    return {
        key: content.id,
        text: `${content.title} [${ContentSubtype[content.subtype]}] [${ContentSource[content.source]}]`
    };
};
